import uniqueId from "lodash/uniqueId";
import * as React from "react";

import { County, CountyWithinRanges, GeographyService, GetNearbyCountiesWithinRange, State, Zip } from "@app/geography";
import { AccordionContainer, Accordion, BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { Link } from "react-router-dom";
import { QESSelect } from "@app/shared/qes-standards";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface CountyDetailsProps {
  lookupCounty: string;
  lookupState: string;
  states: State[];
  updateUrlPath: (state: string, countySSA: string) => void;
}

interface CountyPaneState {
  activeCounty?: County;
  activeState?: State;
  adjacentCounties: CountyWithinRanges[];
  adjacentCountiesWithin50: CountyWithinRanges[];
  adjacentCountiesWithin100: CountyWithinRanges[];
  adjacentCountiesWithin150: CountyWithinRanges[];
  counties: County[];
  zips: Zip[];
  zipsExtendingNonPrimary: Zip[];
  zipsExtendingPrimary: Zip[];
  zipsOnly: Zip[];
}

export default class GeoLookupCountyView extends React.Component<CountyDetailsProps, CountyPaneState> {
  private readonly geographyService = new GeographyService();

  constructor(props: CountyDetailsProps) {
    super(props);

    this.state = {
      adjacentCounties: [],
      adjacentCountiesWithin50: [],
      adjacentCountiesWithin100: [],
      adjacentCountiesWithin150: [],
      counties: [],
      zips: [],
      zipsExtendingNonPrimary: [],
      zipsExtendingPrimary: [],
      zipsOnly: [],
    };
  }

  public async componentDidMount() {
    this.loadCounties();
  }

  public async loadCounties(): Promise<void> {
    if (this.props.lookupCounty.length > 0 && this.props.lookupState.length > 0) {
      const counties = await this.geographyService.getCountiesByState(this.props.lookupState);
      const activeCounty = counties.find((e) => e.countySSA === this.props.lookupCounty);

      let states = this.props.states;
      if (this.props.states.length === 0) {
        states = await this.geographyService.getStates();
      }
      const activeState = this.state.activeState ? this.state.activeState : states.find((e) => e.abbreviation === this.props.lookupState);

      await this.refreshCountyItems(activeCounty, activeState, counties);
    }
  }

  public render() {
    let countiesList;
    let countiesAdjacentList;
    let countiesAdjacent50List;
    let countiesAdjacent100List;
    let countiesAdjacent150List;
    let countiesExtendedIntoList;
    let countiesExtendIntoActiveList;
    let countiesLegacyZipsList;

    if (this.state.activeCounty) {
      if (this.state.zipsOnly.length > 0) {
        countiesList = this.state.zipsOnly.map((zip) =>
          <li className="mtb5 plr10" key={uniqueId(zip.zipCode)}><Link to={`/tools/lookupzip/${zip.zipCode}`}>{zip.zipCode}</Link></li>,
        );
      } else {
        countiesList = this.renderNone();
      }

      if (this.state.adjacentCounties.length > 0) {
        countiesAdjacentList = this.state.adjacentCounties.map((county) =>
          <li className="mtb5 plr10" key={uniqueId(county.name)}><Link to={`/tools/lookupcounty/${county.state}/${county.countySSA}`}>{county.name}, {county.state}</Link></li>,
        );
      } else {
        countiesAdjacentList = this.renderNone();
      }

      if (this.state.adjacentCountiesWithin50.length > 0) {
        countiesAdjacent50List = this.state.adjacentCountiesWithin50.map((county) =>
          <li className="mtb5 plr10" key={uniqueId(county.name)}><Link to={`/tools/lookupcounty/${county.state}/${county.countySSA}`}>{county.name}, {county.state}</Link></li>,
        );
      } else {
        countiesAdjacent50List = this.renderNone();
      }

      if (this.state.adjacentCountiesWithin100.length > 0) {
        countiesAdjacent100List = this.state.adjacentCountiesWithin100.map((county) =>
          <li className="mtb5 plr10" key={uniqueId(county.name)}><Link to={`/tools/lookupcounty/${county.state}/${county.countySSA}`}>{county.name}, {county.state}</Link></li>,
        );
      } else {
        countiesAdjacent100List = this.renderNone();
      }

      if (this.state.adjacentCountiesWithin150.length > 0) {
        countiesAdjacent150List = this.state.adjacentCountiesWithin150.map((county) =>
          <li className="mtb5 plr10" key={uniqueId(county.name)}><Link to={`/tools/lookupcounty/${county.state}/${county.countySSA}`}>{county.name}, {county.state}</Link></li>,
        );
      } else {
        countiesAdjacent150List = this.renderNone();
      }

      if (this.state.zipsExtendingPrimary.length > 0) {
        countiesExtendedIntoList = this.state.zipsExtendingPrimary.map((zip) =>
          <li className="mtb5 plr10" key={uniqueId(zip.zipCode)}><Link to={`/tools/lookupzip/${zip.zipCode}`}>{zip.zipCode}</Link></li>,
        );
      } else {
        countiesExtendedIntoList = this.renderNone();
      }

      if (this.state.zipsExtendingNonPrimary.length > 0) {
        countiesExtendIntoActiveList = this.state.zipsExtendingNonPrimary.map((zip) =>
          <li className="mtb5 plr10" key={uniqueId(zip.zipCode)}><Link to={`/tools/lookupzip/${zip.zipCode}`}>{zip.zipCode}</Link></li>,
        );
      } else {
        countiesExtendIntoActiveList = this.renderNone();
      }
      if (this.state.zips.find((e) => e.isLegacy === true)) {
        countiesLegacyZipsList = this.state.zips.map((zip) => zip.isLegacy === true &&
          <li className="mtb5 plr10" key={uniqueId(zip.zipCode)}><Link to={`/tools/lookupzip/${zip.zipCode}`}>{zip.zipCode}</Link></li>,
        );
      } else {
        countiesLegacyZipsList = this.renderNone();
      }
    }

    return (
      <>
        {(this.state.activeCounty) && (
          <Page documentTitle="Lookup a County">
            <Breadcrumbs
              template={BreadcrumbTemplateEnum.Tools}
              items={[
                {
                  iconClass: "fa-search",
                  text: "Lookup a County",
                },
              ]}
            />
            <PageInner>
              <PageHeader title={this.state.activeCounty.name} disableHR={true} iconName={QesIcons.FontAwesome.Search}>
                <PageHeaderLeft>
                  <span className="geolookup-subtitle">{this.state.activeCounty.state}</span>
                </PageHeaderLeft>
                <PageHeaderRight>
                  <div className="page-title-bar">
                    <div className="ml-auto">
                      <div className="d-block d-md-inline-block">
                        <QESSelect label="County SSA" hideLabel={true} testId="geolookup__county-ssa" className="form-control lookup-state-option mb-3 mb-md-0" value={this.state.activeCounty.countySSA} onChange={this.handleCountyChange}>
                          {!this.state.counties || this.state.counties.length === 0 && (<option>Loading...</option>)}
                          {this.state.counties.map((county) => (
                            <option key={county.countySSA} value={county.countySSA}>{county.name}</option>
                          ))}
                        </QESSelect>
                      </div>
                      <div className="d-block d-md-inline-block mt-3 mt-md-0 mb-3 mb-md-0 ml-md-3">
                        <QESSelect label="State" hideLabel={true} testId="geolookup__state" className="form-control lookup-state-option mb-3 mb-md-0" value={this.state.activeState.abbreviation} onChange={this.handleStateChange}>
                          {!this.props.states || this.props.states.length === 0 && (<option>Loading...</option>)}
                          {this.props.states.map((state) => (
                            <option key={state.abbreviation} value={state.abbreviation}>{state.name}</option>
                          ))}
                        </QESSelect>
                      </div>
                    </div>
                  </div>
                </PageHeaderRight>
              </PageHeader>
              <PageSection>
                <div className="row">
                  <div className="col-12 col-sm-4">
                    <div className="card card-expansion-item card-static">
                      <div className="card-header">County Details</div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-12">
                            <p><strong>SSA:</strong> {this.state.activeCounty.countySSA}</p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <p><strong>FIPS:</strong> {this.state.activeCounty.countyFIPS}</p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <p><strong>Area:</strong> {this.state.activeCounty.area} mi<sup>2</sup></p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-12">
                                <strong>Extents:</strong>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="pl-3">Minimum Latitude:</em> {this.state.activeCounty.minLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="pl-3">Minimum Longitude:</em> {this.state.activeCounty.minLongitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="pl-3">Maximum  Latitude:</em> {this.state.activeCounty.maxLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <p className="pl-3"><em>Maximum  Longitude:</em> {this.state.activeCounty.maxLongitude}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-12">
                                <strong>Centroid:</strong>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="pl-3">Latitude:</em> {this.state.activeCounty.centroidLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <p className="pl-3"><em>Longitude:</em> {this.state.activeCounty.centroidLongitude}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-12">
                                <strong>Label:</strong>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="pl-3">Latitude:</em> {this.state.activeCounty.labelLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="pl-3">Longitude:</em> {this.state.activeCounty.labelLongitude}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-8">
                    <AccordionContainer>
                      <Accordion title="Zips" defaultOpen={true} testId="geo-lookup-county-view__zips-accordion">
                        <>
                          <p className="mb0">
                            Zip Codes only in <strong>{this.state.activeCounty.name}</strong> county...
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesList}
                          </ul>
                          <p className="mb0">
                            Zip Codes primarily in <strong>{this.state.activeCounty.name}</strong> county, but extending into other counties...
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesExtendedIntoList}
                          </ul>
                          <p className="mb0">
                            Zip Codes in other counties, but extending into <strong>{this.state.activeCounty.name}</strong> county...
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesExtendIntoActiveList}
                          </ul>
                        </>
                      </Accordion>
                      <Accordion title="Legacy Zips" testId="geo-lookup-county-view__legacy-zips-accordion">
                        <>
                          <p className="mb0">
                            Zip Codes in <strong>{this.state.activeCounty.name}</strong> county that are no longer valid, but have been retained for geocoding purposes...
                          </p>
                          <ul className="multi-column list-unstyled mb0 columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesLegacyZipsList}
                          </ul>
                        </>
                      </Accordion>
                      <Accordion title="Nearby Counties" testId="geo-lookup-county-view__nearby-counties-accordion">
                        <>
                          <p className="mb0">
                            Counties adjacent to <strong>{this.state.activeCounty.name}</strong>.
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesAdjacentList}
                          </ul>
                          <p className="mb0">
                            Counties within 50 miles of <strong>{this.state.activeCounty.name}</strong>.
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesAdjacent50List}
                          </ul>
                          <p className="mb0">
                            Counties within 100 miles of <strong>{this.state.activeCounty.name}</strong>.
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesAdjacent100List}
                          </ul>
                          <p className="mb0">
                            Counties within 150 miles of <strong>{this.state.activeCounty.name}</strong>.
                          </p>
                          <ul className="multi-column list-unstyled columns-xs-2 columns-sm-3 columns-md-4 columns-lg-5">
                            {countiesAdjacent150List}
                          </ul>
                        </>
                      </Accordion>
                    </AccordionContainer>
                  </div>
                </div>
              </PageSection>
            </PageInner>
          </Page>
        )}
      </>
    );
  }

  private renderNone() {
    return (<li className="mtb5 plr10"><strong className="plr10">&lt;None&gt;</strong></li>);
  }

  private async refreshCountyItems(activeCounty: County, activeState: State, counties: County[]) {
    const countySSAs: string[] = [];
    countySSAs.push(activeCounty.countySSA);

    await this.getNearbyCounties(countySSAs);
    await this.getZipsExtending(activeCounty);

    this.setState((prevState) => ({ ...prevState, activeCounty, activeState, counties }),
      () => this.props.updateUrlPath(this.state.activeState.abbreviation, this.state.activeCounty.countySSA));
  }

  private async getNearbyCounties(countySSAs: string[]) {
    const nearbyCountiesWithinRange = await this.geographyService.getNearbyCountiesWithinRange({ countySSAs } as GetNearbyCountiesWithinRange);
    const adjacentCounties = nearbyCountiesWithinRange.filter((a) => a.range === 0);
    const adjacentCountiesWithin50 = nearbyCountiesWithinRange.filter((a) => a.range === 50);
    const adjacentCountiesWithin100 = nearbyCountiesWithinRange.filter((a) => a.range === 100);
    const adjacentCountiesWithin150 = nearbyCountiesWithinRange.filter((a) => a.range === 150);

    this.setState((prevState) => ({ ...prevState, adjacentCounties, adjacentCountiesWithin50, adjacentCountiesWithin100, adjacentCountiesWithin150 }));
  }

  private async getZipsExtending(activeCounty: County) {
    const zipsExtendingBoth = await this.geographyService.getExtendingZipsByCounty(activeCounty.countySSA);
    const zipsExtendingNonPrimary = zipsExtendingBoth.filter((e) => e.isPrimaryCounty === false);
    const zipsExtendingPrimary = zipsExtendingBoth.filter((e) => e.isPrimaryCounty === true);

    const zips = await this.geographyService.getZipsByCounty(activeCounty.countySSA);
    let zipsOnly: Zip[] = zips;
    if (zipsExtendingBoth.length > 0) {
      zipsOnly = zips.filter((e) => zipsExtendingBoth.every((f) => e.zipCode !== f.zipCode));
    }

    this.setState((prevState) => ({ ...prevState, zips, zipsExtendingNonPrimary, zipsExtendingPrimary, zipsOnly }));
  }

  private handleCountyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countySSA = e.target.value;
    const activeCounty = this.state.counties.find((s) => s.countySSA === countySSA);

    this.refreshCountyItems(activeCounty, this.state.activeState, this.state.counties);
  }

  private handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const abbreviation = e.target.value;
    const activeState = this.props.states.find((s) => s.abbreviation === abbreviation);

    this.refreshStateItems(activeState);
  }

  private async refreshStateItems(active: State) {
    const activeState = active;
    const counties = await this.geographyService.getCountiesByState(activeState.abbreviation);
    const activeCounty = counties[0];

    this.refreshCountyItems(activeCounty, activeState, counties);
  }
}

export { GeoLookupCountyView };
